/** */
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addDataToStore } from 'store/actions/user-data';
import { Alert } from 'app/shared/form-alert';
import { Icon } from 'assets/icons';
import { isEmailValid } from 'app/shared/utils/input';
import { generateUniqueId } from 'app/shared/utils/general';
import { useGuestService } from 'hooks/guest';
import { OTPForm } from '../otp';
import { toast } from 'react-toastify';
import { urls } from 'services/_url';
import styles from './login.module.css';

const LoginForm = () => {
  const dispatch = useDispatch();
  const history = useNavigate();

  const { login } = useGuestService();
  const [alert, setAlert] = useState({});
  const [form_data, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [show_password, setShowPassword] = useState(false);
  const [remember_me, setRememberMe] = useState(false);
  const [show_otp_form, setShowOtpForm] = useState(false);
  const [email_to_resend, setEmailToResend] = useState('');
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });

  const validateField = useCallback(
    (fieldName, value) => {
      let errorMessage = '';
      switch (fieldName) {
        case 'email':
          if (!value.trim()) {
            errorMessage = 'Email address is required.';
          } else if (!isEmailValid(value)) {
            errorMessage = 'Invalid email address.';
          }
          break;
        case 'password':
          if (!value.trim()) {
            errorMessage = 'Password is required.';
          } else if (value.length < 8) {
            errorMessage = 'Password must contain 8 or more characters.';
          }
          break;
        default:
          break;
      }
      setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: errorMessage }));
    },
    [errors]
  );

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, email: value }));
    validateField('email', value);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, password: value }));
    validateField('password', value);
  };

  const toggleRememberMe = () => setRememberMe((initialValue) => !initialValue);

  const isFormDisabled = () => {
    const hasErrors = Object.values(errors).some((error) => error !== '');
    const isFieldsIncomplete = Object.values(form_data).some((value) => !value?.trim());
    return hasErrors || isFieldsIncomplete;
  };

  const submitForm = (e) => {
    e?.preventDefault();
    if (isFormDisabled()) {
      validateField('email', form_data.email);
      validateField('password', form_data.password);
      return;
    }

    setLoading(true);
    login({ data: form_data })
      .then((response) => {
        const { error, payload } = response;
        if (error) {
          setAlert({ message: error, id: generateUniqueId() });
          return;
        }
        const { otp_required } = payload;
        if (otp_required) {
          toast.success('OTP sent to your email address.');
          setEmailToResend(form_data.email);
          setShowOtpForm(true);
        }

        dispatch(addDataToStore({ ...payload, remember_me, logged_in_time: Date.now() }));
        history('/dashboard');
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className={styles.pageWrapper}>
      {/* Home button */}
      <a href={urls.landing} className={styles.homeBtn}>
        <Icon name="home" width={16} height={16} />
        Home
      </a>

      <div className={styles.formInner}>
        {show_otp_form ? (
          <div className={styles.otpWrap}>
            <OTPForm email={email_to_resend} />
          </div>
        ) : (
          <>
            <h1 className={styles.formHeading}>Welcome back</h1>
            <p className={styles.formSubhead}>
              Need an account?{' '}
              <a
                href="/register"
                onClick={(e) => {
                  e.preventDefault();
                  history('/register');
                }}
              >
                Create one free →
              </a>
            </p>

            {alert.message && (
              <div className={styles.alertWrap}>
                <Alert {...alert} />
              </div>
            )}

            <form onSubmit={submitForm}>
              <div className={styles.fieldGroup}>
                {/* Email */}
                <div>
                  <label className={styles.fieldLabel} htmlFor="login-email">
                    Email address
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@example.com"
                    value={form_data.email}
                    onChange={handleEmailChange}
                    className={`${styles.input}${errors.email ? ` ${styles.inputError}` : ''}`}
                  />
                  {errors.email && <p className={styles.fieldError}>{errors.email}</p>}
                </div>

                {/* Password */}
                <div>
                  <label className={styles.fieldLabel} htmlFor="login-password">
                    Password
                  </label>
                  <div className={styles.inputPwWrap}>
                    <input
                      id="login-password"
                      type={show_password ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={form_data.password}
                      onChange={handlePasswordChange}
                      className={`${styles.input} ${styles.inputPwPad}${
                        errors.password ? ` ${styles.inputError}` : ''
                      }`}
                    />
                    <button
                      type="button"
                      className={styles.pwToggle}
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                      aria-label={show_password ? 'Hide password' : 'Show password'}
                    >
                      <Icon name={show_password ? 'hide' : 'show'} width={18} height={18} />
                    </button>
                  </div>
                  {errors.password && <p className={styles.fieldError}>{errors.password}</p>}
                </div>
              </div>

              {/* Remember me / Forgot password */}
              <div className={styles.rememberRow}>
                <label className={styles.checkLabel}>
                  <input type="checkbox" checked={remember_me} onChange={toggleRememberMe} />
                  Remember me
                </label>
                <a
                  href="/password/recovery"
                  className={styles.forgotLink}
                  onClick={(e) => {
                    e.preventDefault();
                    history('/password/recovery');
                  }}
                >
                  Forgot password?
                </a>
              </div>

              <button type="submit" className={styles.submitBtn} disabled={loading}>
                {loading ? 'Logging in…' : 'Log in →'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginForm;
